// client/src/components/pages/LexiStar/FleetTargets/ProgressView.tsx (ST-00248)
//
// Actual against target: one card per target (a metric for the organisation, a route or a
// truck) with the window's figures — actual, target, variance and whether it is on target (a
// glyph and a word, never colour alone) — a chart of the actual per period (bars) against the
// target (a line, one axis: the same unit), and the periods as a table. A running period is
// judged to date (the server's rule: the target of the days so far). Figures from
// GET /targets/progress; the page formats, it computes nothing.

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cardClasses } from '../Dashboard/analyticsAtoms';
import type { FleetGrain, TargetMetric, TargetProgressResponse, TargetSeries } from '../../../../types/lexistar-performance';
import {
  OUTCOME_META,
  directionText,
  fmtMetricValue,
  fmtVariance,
  periodsMet,
  progressPoints,
  scopeText,
  seriesSummary,
  type ProgressPoint,
} from './targetsLogic';
import { ACTUAL_BAR, AXIS, CURSOR, GRID, RADIUS_V, TARGET_LINE, TICK, TOOLTIP_LABEL, TOOLTIP_STYLE } from './chartTheme';

function OutcomeChip({ outcome }: { outcome: keyof typeof OUTCOME_META }) {
  const m = OUTCOME_META[outcome];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.pill}`}>
      <span aria-hidden="true">{m.glyph}</span>
      {m.label}
    </span>
  );
}

const compactTick = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toLocaleString('en-GB', { maximumFractionDigits: 1 })}M`;
  if (a >= 1e3) return `${(v / 1e3).toLocaleString('en-GB', { maximumFractionDigits: 1 })}K`;
  return v.toLocaleString('en-GB', { maximumFractionDigits: 1 });
};

function SeriesChart({ s, points, titleId }: { s: TargetSeries; points: ProgressPoint[]; titleId: string }) {
  return (
    <div role="img" aria-labelledby={titleId} style={{ height: 200 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }} barCategoryGap="30%">
          {[
            <CartesianGrid key="grid" stroke={GRID} vertical={false} />,
            <XAxis key="x" dataKey="tick" tick={TICK} stroke={AXIS} minTickGap={8} />,
            <YAxis
              key="y"
              width={44}
              tick={TICK}
              stroke={AXIS}
              tickFormatter={compactTick}
              domain={s.unit === '%' ? [0, 100] : [0, 'auto']}
            />,
            <Tooltip
              key="tip"
              cursor={CURSOR}
              contentStyle={TOOLTIP_STYLE}
              labelStyle={TOOLTIP_LABEL}
              labelFormatter={(_l, payload) => (payload && payload[0] ? String((payload[0].payload as ProgressPoint).label) : '')}
              formatter={(v, name) => [fmtMetricValue(s.unit, v === null || v === undefined ? null : Number(v)), name]}
              isAnimationActive={false}
            />,
            <Bar key="actual" dataKey="actual" name="Actual" fill={ACTUAL_BAR} maxBarSize={24} radius={RADIUS_V} isAnimationActive={false} />,
            <Line
              key="target"
              type="linear"
              dataKey="targetLine"
              name="Target"
              stroke={TARGET_LINE}
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface)', fill: TARGET_LINE }}
              connectNulls={false}
              isAnimationActive={false}
            />,
            // The running period: its target so far as a lone hollow mark, not a drop in the line.
            <Line
              key="so-far"
              type="linear"
              dataKey="targetSoFar"
              name="Target so far"
              stroke={TARGET_LINE}
              strokeWidth={0}
              dot={{ r: 5, strokeWidth: 2, stroke: TARGET_LINE, fill: 'var(--surface)' }}
              activeDot={{ r: 5, strokeWidth: 2, stroke: TARGET_LINE, fill: 'var(--surface)' }}
              connectNulls={false}
              isAnimationActive={false}
              legendType="none"
            />,
          ]}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function SeriesCard({ s, grain, metric }: { s: TargetSeries; grain: FleetGrain; metric?: TargetMetric }) {
  const points = progressPoints(s, grain);
  const t = s.totals;
  const { met, judged } = periodsMet(s);
  const titleId = `progress-${s.metric}-${s.scope_type}-${s.scope_id}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  const word = grain === 'day' ? 'days' : grain === 'week' ? 'weeks' : 'months';
  return (
    <article className={`${cardClasses} p-4 space-y-3`} aria-labelledby={titleId}>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 id={titleId} className="text-sm font-semibold text-slate-900">
            {s.label}
          </h3>
          <p className="text-xs text-slate-600 truncate">
            {scopeText(s)} · {directionText(s)}
          </p>
        </div>
        <OutcomeChip outcome={t.outcome} />
      </header>
      <dl className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="text-xs text-slate-500">Actual</dt>
          <dd className="font-semibold text-slate-900">{fmtMetricValue(s.unit, t.actual)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">{t.partial ? 'Target so far' : 'Target'}</dt>
          <dd className="font-semibold text-slate-900">{fmtMetricValue(s.unit, t.target_to_date)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Difference</dt>
          <dd className="font-semibold text-slate-900">
            {fmtVariance(s.unit, t.variance)}
            {t.variance_pct !== null && s.unit !== '%' && (
              <span className="ml-1 text-xs font-normal text-slate-500">
                ({t.variance_pct > 0 ? '+' : t.variance_pct < 0 ? '−' : '±'}
                {Math.abs(t.variance_pct).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%)
              </span>
            )}
          </dd>
        </div>
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
        <ul className="flex items-center gap-4" aria-label="Legend">
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm bg-primary-600" /> Actual
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-0.5 w-4 bg-slate-700" /> Target
          </li>
          {points.some((p) => p.partial && p.targetSoFar !== null) && (
            <li className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-full border-2 border-slate-700 bg-surface" /> Target so far
            </li>
          )}
        </ul>
        {judged > 0 && (
          <span>
            {met} of {judged} {word} on target
          </span>
        )}
      </div>
      <p className="sr-only">{seriesSummary(s, grain)}</p>
      <SeriesChart s={s} points={points} titleId={titleId} />
      <details className="text-xs">
        <summary className="cursor-pointer select-none text-slate-600 hover:text-slate-900">The numbers per period</summary>
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th scope="col" className="py-1 pr-3 font-medium">Period</th>
                <th scope="col" className="py-1 px-2 font-medium text-right">Actual</th>
                <th scope="col" className="py-1 px-2 font-medium text-right">Target</th>
                <th scope="col" className="py-1 px-2 font-medium text-right">Difference</th>
                <th scope="col" className="py-1 pl-2 font-medium">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {points.map((p) => (
                <tr key={p.start}>
                  <th scope="row" className="py-1 pr-3 text-left font-normal text-slate-800 whitespace-nowrap">
                    {p.label}
                  </th>
                  <td className="py-1 px-2 text-right tabular-nums text-slate-800">{fmtMetricValue(s.unit, p.actual)}</td>
                  <td className="py-1 px-2 text-right tabular-nums text-slate-600">{fmtMetricValue(s.unit, p.target)}</td>
                  <td className="py-1 px-2 text-right tabular-nums text-slate-600">{fmtVariance(s.unit, p.variance)}</td>
                  <td className="py-1 pl-2 whitespace-nowrap">
                    <span aria-hidden="true">{OUTCOME_META[p.outcome].glyph}</span> {OUTCOME_META[p.outcome].label}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {metric && <p className="mt-2 text-slate-500">{metric.description}</p>}
      </details>
    </article>
  );
}

export default function ProgressView({
  res,
  metrics,
  canManage,
  onSetTargets,
}: {
  res: TargetProgressResponse;
  metrics: readonly TargetMetric[];
  canManage: boolean;
  onSetTargets: () => void;
}) {
  const byKey = new Map(metrics.map((m) => [m.key, m]));
  return (
    <div className="space-y-4">
      {res.unavailable.length > 0 && (
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Some actuals could not be read: {[...new Set(res.unavailable.map((u) => u.reason))].join('; ')}
        </div>
      )}
      {res.series.length === 0 ? (
        <div className={`${cardClasses} p-6 text-center`}>
          <h2 className="text-base font-medium text-slate-900">No targets for this period</h2>
          <p className="mt-1 text-sm text-slate-600">
            Set a target for trips, tonnes, kilometres, fuel, on-time trips, utilisation or availability, and its actual
            appears here against it.
          </p>
          {canManage && (
            <button
              type="button"
              onClick={onSetTargets}
              className="mt-3 inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
            >
              Set targets
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {res.series.map((s) => (
            <SeriesCard key={`${s.metric}|${s.scope_type}|${s.scope_id}`} s={s} grain={res.grain} metric={byKey.get(s.metric)} />
          ))}
        </div>
      )}
    </div>
  );
}
